import React, { useState, useCallback, useRef, useEffect } from 'react';
import { calculatePosition, calculateWidth, formatDate } from '../../utils';
import DecisionOptionsModal, { DEFAULT_OPTIONS } from '../../../FlowchartView/TaskNode/DecisionOptionsModal';
import { useGanttBarDrag } from '../../hooks/useGanttBarDrag';
import GanttBarDecisions from '../GanttBarDecisions';

// Minimum bar width (in pixels) to show inline decision buttons
const MIN_WIDTH_FOR_INLINE_DECISIONS = 180;

/**
 * GanttBar Component
 * 
 * The horizontal bar representing a task's duration on the timeline.
 * Supports:
 * - Click to select (single)
 * - Ctrl/Cmd+click to toggle selection
 * - Shift+click to add to selection
 * - Drag to move (moves all selected bars if multiple selected)
 * - Drag edges to resize (single bar only)
 */
const GanttBar = ({
  task,
  taskId,
  taskName,
  startDate,
  endDate,
  dueDate = null, // For diamond marker
  hasEstimate = false, // Whether bar width is from estimated duration (minutes)
  viewStartDate,
  totalDays,
  statusColor,
  isLocked = false,
  onDateChange,
  onDueDateChange, // Handler for dragging the diamond
  isSelected = false,
  onSelect,
  onToggleSelect,
  // Multi-drag support
  selectedTaskIds,
  onMultiDrag,
  multiDragDelta = 0,
  isDraggingMultiple = false,
  multiDragType = null,
  // Zoom level for snap behavior
  zoomLevel = 'day',
  // Decision handlers
  onDecisionAnswerChange,
  onDecisionOptionsChange,
}) => {
  // Decision state
  const isDecision = task?.nodeType === 'decision';
  const options = task?.options || DEFAULT_OPTIONS;
  const answer = task?.answer || null;
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [barPixelWidth, setBarPixelWidth] = useState(0);
  const [popoutPosition, setPopoutPosition] = useState(null);
  const barRef = useRef(null);

  // Calculate base position and width as percentages
  const baseLeftPercent = calculatePosition(startDate, viewStartDate, totalDays);
  const baseWidthPercent = calculateWidth(startDate, endDate, viewStartDate, totalDays);
  
  // Calculate due date diamond position (if due date exists)
  const dueDatePercent = dueDate ? calculatePosition(dueDate, viewStartDate, totalDays) : null;

  // Use drag hook for all drag-related logic
  const {
    isDragging,
    isMultiDragging,
    leftPercent,
    widthPercent,
    hasDraggedRef,
    handleMouseDown,
  } = useGanttBarDrag({
    taskId,
    startDate,
    endDate,
    dueDate,
    hasEstimate,
    viewStartDate,
    totalDays,
    baseLeftPercent,
    baseWidthPercent,
    zoomLevel,
    isSelected,
    selectedTaskIds,
    onDateChange,
    onDueDateChange,
    onMultiDrag,
    isDraggingMultiple,
    multiDragDelta,
    multiDragType,
    barRef,
  });

  // Format dates for tooltip
  const dateRange = `${formatDate(startDate, 'full')} - ${formatDate(endDate, 'full')}`;
  const dueDateStr = dueDate ? formatDate(dueDate, 'fullWithTime') : null;
  // Measure bar width for deciding inline vs popout decision buttons
  useEffect(() => {
    if (!barRef.current) return;
    const updateWidth = () => {
      if (barRef.current) {
        setBarPixelWidth(barRef.current.offsetWidth);
      }
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(barRef.current);
    return () => observer.disconnect();
  }, [widthPercent]);

  // Determine if we have enough space for inline decision buttons
  const showInlineDecisions = isDecision && barPixelWidth >= MIN_WIDTH_FOR_INLINE_DECISIONS;
  const showPopoutDecisions = isDecision && !showInlineDecisions && isSelected;
  
  // Show lock icon inside bar when wide enough (80px minimum)
  const showLockIcon = isLocked && barPixelWidth >= 80;

  // Calculate popout position when showing popout (for portal rendering)
  useEffect(() => {
    if (showPopoutDecisions && barRef.current) {
      const rect = barRef.current.getBoundingClientRect();
      setPopoutPosition({
        top: rect.bottom + 4,
        left: rect.left + rect.width / 2,
      });
    } else {
      setPopoutPosition(null);
    }
  }, [showPopoutDecisions, leftPercent, widthPercent]);

  // Decision options change handler
  const handleOptionsChange = useCallback((newOptions) => {
    onDecisionOptionsChange?.(taskId, newOptions);
    if (answer && !newOptions.find(opt => opt.id === answer)) {
      onDecisionAnswerChange?.(taskId, null);
    }
  }, [taskId, answer, onDecisionAnswerChange, onDecisionOptionsChange]);

  // Handle click for selection
  const handleClick = useCallback((e) => {
    if (hasDraggedRef.current) return;
    
    e.stopPropagation();
    
    if (e.shiftKey || e.ctrlKey || e.metaKey) {
      onToggleSelect?.(taskId, true);
    } else {
      onSelect?.(taskId);
    }
  }, [taskId, onSelect, onToggleSelect, hasDraggedRef]);

  // Build class names
  // NOTE: 'gantt-bar--selected' is NOT included here - it's managed via DOM manipulation
  // in useGanttSelection for instant visual feedback. React state still tracks selection
  // for other purposes (bulk actions, etc.) but the visual class is DOM-driven.
  const barClasses = [
    'gantt-bar',
    isDragging && 'gantt-bar--dragging',
    isMultiDragging && 'gantt-bar--multi-dragging',
    onDateChange && 'gantt-bar--editable',
    isLocked && 'gantt-bar--locked',
    isDecision && 'gantt-bar--decision',
    showPopoutDecisions && 'gantt-bar--decision-popout',
  ].filter(Boolean).join(' ');

  return (
    <>
      <div
        ref={barRef}
        className={barClasses}
        data-task-id={taskId}
        style={{
          left: `${leftPercent}%`,
          width: `${widthPercent}%`,
          backgroundColor: statusColor,
          borderColor: statusColor,
        }}
        onClick={handleClick}
        title={`${taskName}\n${dateRange}${dueDateStr ? `\nDue: ${dueDateStr}` : ''}`}
      >
        {/* Left resize handle - changes start date */}
        {onDateChange && (
          <div 
            className="gantt-bar__handle gantt-bar__handle--left"
            onMouseDown={(e) => handleMouseDown(e, 'resize-start')}
            title="Drag to change start date"
          />
        )}
        
        {/* Main draggable area - changes start date (shifts entire bar) */}
        <div 
          className="gantt-bar__content"
          onMouseDown={(e) => handleMouseDown(e, 'move')}
        >
          {/* Decision indicator for narrow bars */}
          {isDecision && !showInlineDecisions && (
            <span className="gantt-bar__decision-icon">◇</span>
          )}
          <span className="gantt-bar__label">{taskName}</span>
          
          {/* Inline decision buttons (when bar is wide enough) */}
          {showInlineDecisions && (
            <GanttBarDecisions
              taskId={taskId}
              options={options}
              answer={answer}
              showInline={true}
              showPopout={false}
              popoutPosition={null}
              onAnswerChange={onDecisionAnswerChange}
              onOpenOptionsModal={() => setShowOptionsModal(true)}
            />
          )}
        </div>
        
        {/* Lock icon (when bar is wide enough) */}
        {showLockIcon && (
          <div className="gantt-bar__lock-indicator">
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              className="gantt-bar__lock-icon"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
        )}
        
        {/* Right resize handle - changes estimated days */}
        {onDateChange && (
          <div 
            className="gantt-bar__handle gantt-bar__handle--right"
            onMouseDown={(e) => handleMouseDown(e, 'resize-end')}
            title="Drag to change estimated days"
          />
        )}
      </div>
      
      {/* Due date diamond marker - positioned absolutely on timeline */}
      {dueDatePercent !== null && (
        <div
          className={`gantt-bar__due-diamond ${onDueDateChange ? 'gantt-bar__due-diamond--draggable' : ''}`}
          style={{
            left: `${dueDatePercent}%`,
            borderColor: statusColor,
          }}
          onMouseDown={onDueDateChange ? (e) => handleMouseDown(e, 'move-due') : undefined}
          title={`Due: ${dueDateStr}`}
        >
          <svg viewBox="0 0 12 12" className="gantt-bar__due-diamond-icon">
            <path d="M6 0 L12 6 L6 12 L0 6 Z" fill={statusColor} />
          </svg>
        </div>
      )}
      
      {/* Popout decision buttons */}
      {showPopoutDecisions && (
        <GanttBarDecisions
          taskId={taskId}
          options={options}
          answer={answer}
          showInline={false}
          showPopout={true}
          popoutPosition={popoutPosition}
          onAnswerChange={onDecisionAnswerChange}
          onOpenOptionsModal={() => setShowOptionsModal(true)}
        />
      )}
      
      {/* Decision Options Modal */}
      {showOptionsModal && (
        <DecisionOptionsModal
          isOpen={showOptionsModal}
          options={options}
          onOptionsChange={handleOptionsChange}
          onClose={() => setShowOptionsModal(false)}
        />
      )}
    </>
  );
};

export default GanttBar;
